import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageCircle, Send, Trash2, Image, X, FileText, ChevronLeft, ChevronRight, Pencil, Check, Video, AtSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_IMAGES = 5;

export default function CommunityPage({ user }) {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [postImagePreviews, setPostImagePreviews] = useState([]);
  const [postFile, setPostFile] = useState(null);
  const [postVideo, setPostVideo] = useState(null);
  const [postVideoPreview, setPostVideoPreview] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [allMembers, setAllMembers] = useState([]);
  const [tagQuery, setTagQuery] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagCursorPos, setTagCursorPos] = useState(0);
  const textareaRef = React.useRef(null);

  const renderContentWithLinks = (content) => {
    if (!content) return null;
    const pattern = /(https?:\/\/[^\s]+)|(@\[([^\]]+)\]\(([^)]+)\))/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(content)) !== null) {
      if (match.index > lastIndex) parts.push(content.slice(lastIndex, match.index));
      if (match[1]) {
        parts.push(
          <a key={`link-${match.index}`} href={match[1]} target="_blank" rel="noopener noreferrer"
            className="text-[#5b7f95] hover:text-[#4a6b80] underline break-all">{match[1]}</a>
        );
      } else if (match[2]) {
        parts.push(
          <Link key={`tag-${match.index}`} to={`/directory?member=${match[4]}`}
            className="text-[#cb9944] font-semibold hover:underline cursor-pointer">@{match[3]}</Link>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) parts.push(content.slice(lastIndex));
    return parts.length > 0 ? parts : content;
  };

  const handlePostInputChange = (e) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewPost(val);
    setTagCursorPos(cursorPos);

    // Detect @mention
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setTagQuery(atMatch[1].toLowerCase());
      setShowTagDropdown(true);
    } else {
      setShowTagDropdown(false);
      setTagQuery('');
    }
  };

  const insertTag = (member) => {
    const textBeforeCursor = newPost.slice(0, tagCursorPos);
    const textAfterCursor = newPost.slice(tagCursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const name = member.display_name || member.full_name;
    const tag = `@[${name}](${member.id}) `;
    const newText = textBeforeCursor.slice(0, atIndex) + tag + textAfterCursor;
    setNewPost(newText);
    setShowTagDropdown(false);
    setTagQuery('');
    if (textareaRef.current) textareaRef.current.focus();
  };

  const filteredTagMembers = allMembers.filter(m => {
    if (!tagQuery) return true;
    const name = (m.display_name || m.full_name || '').toLowerCase();
    const biz = (m.business_name || '').toLowerCase();
    return name.includes(tagQuery) || biz.includes(tagQuery);
  }).slice(0, 8);

  useEffect(() => {
    fetchPosts();
    if (user.is_admin || user.is_area_manager) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch members for tagging:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
      localStorage.setItem('lastViewedPosts', new Date().toISOString());
    } catch (error) {
      toast.error('Failed to load posts');
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = MAX_IMAGES - postImages.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    const selected = files.slice(0, remaining);
    const valid = [];
    const previews = [];
    for (const file of selected) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        continue;
      }
      valid.push(file);
      previews.push(URL.createObjectURL(file));
    }
    if (valid.length + postImages.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    setPostImages([...postImages, ...valid]);
    setPostImagePreviews([...postImagePreviews, ...previews]);
    if (files.length > remaining) {
      toast.info(`Only ${remaining} more image${remaining > 1 ? 's' : ''} allowed`);
    }
    e.target.value = '';
  };

  const removeImage = (index) => {
    setPostImages(postImages.filter((_, i) => i !== index));
    setPostImagePreviews(postImagePreviews.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') { toast.error('Please select a PDF file'); return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('PDF must be less than 10MB'); return; }
      setPostFile(file);
    }
  };

  const removeFile = () => { setPostFile(null); };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please select a video file'); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error('Video must be less than 50MB'); return; }
    setPostVideo(file);
    setPostVideoPreview(URL.createObjectURL(file));
  };

  const removeVideo = () => {
    setPostVideo(null);
    if (postVideoPreview) URL.revokeObjectURL(postVideoPreview);
    setPostVideoPreview(null);
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim() && postImages.length === 0 && !postFile && !postVideo) return;

    setLoading(true);
    try {
      let imageUrls = [];
      let fileUrl = null;
      let fileName = null;
      let videoUrl = null;

      // Upload all images
      for (const img of postImages) {
        const formData = new FormData();
        formData.append('file', img);
        const uploadRes = await axios.post(`${API}/upload/post-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrls.push(uploadRes.data.image_url);
      }

      // Upload video if present
      if (postVideo) {
        const formData = new FormData();
        formData.append('file', postVideo);
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        videoUrl = uploadRes.data.video_url;
      }

      // Upload PDF if present
      if (postFile) {
        const formData = new FormData();
        formData.append('file', postFile);
        const uploadRes = await axios.post(`${API}/upload/post-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.file_url;
        fileName = postFile.name;
      }

      await axios.post(`${API}/posts`, {
        content: newPost,
        image: imageUrls[0] || null,
        images: imageUrls,
        video: videoUrl,
        file_url: fileUrl,
        file_name: fileName
      });
      setNewPost('');
      setPostImages([]);
      setPostImagePreviews([]);
      setPostFile(null);
      removeVideo();
      fetchPosts();
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try { await axios.post(`${API}/posts/${postId}/like`); fetchPosts(); }
    catch (error) { toast.error('Failed to like post'); }
  };

  const handleComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;
    try {
      await axios.post(`${API}/posts/${postId}/comment`, { content });
      setCommentInputs({ ...commentInputs, [postId]: '' });
      fetchPosts();
    } catch (error) { toast.error('Failed to add comment'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try { await axios.delete(`${API}/posts/${postId}`); fetchPosts(); toast.success('Post deleted'); }
    catch (error) { toast.error('Failed to delete post'); }
  };

  const handleEditStart = (post) => {
    setEditingPostId(post.id);
    setEditContent(post.content || '');
  };

  const handleEditSave = async (postId) => {
    try {
      await axios.put(`${API}/posts/${postId}`, { content: editContent });
      setEditingPostId(null);
      setEditContent('');
      fetchPosts();
      toast.success('Post updated!');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleEditCancel = () => {
    setEditingPostId(null);
    setEditContent('');
  };

  const openLightbox = (allImages, startIndex) => {
    setLightbox({ open: true, images: allImages, index: startIndex });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, images: [], index: 0 });
  };

  const getPostImages = (post) => {
    if (post.images && post.images.length > 0) return post.images;
    if (post.image) return [post.image];
    return [];
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-[#2d3748] mb-8">Community Feed</h1>

      {/* Create Post - Admin and Chapter Leaders only */}
      {(user.is_admin || user.is_area_manager) && (
      <Card className="border-gray-100 rounded-2xl shadow-sm mb-8">
        <CardContent className="pt-6">
          <form onSubmit={handleCreatePost}>
            <div className="flex items-start gap-3 mb-4">
              <Avatar>
                <AvatarImage src={user.profile_image ? getImageUrl(user.profile_image) : undefined} />
                <AvatarFallback className="bg-[#5b7f95] text-white">
                  {user.full_name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="relative flex-1">
                <Textarea
                  ref={textareaRef}
                  value={newPost}
                  onChange={handlePostInputChange}
                  placeholder="Share something with the community... Type @ to tag a member"
                  className="w-full bg-gray-50 border-gray-200 rounded-xl resize-none"
                  rows={3}
                  data-testid="new-post-textarea"
                />
                {showTagDropdown && filteredTagMembers.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="tag-dropdown">
                    {filteredTagMembers.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => insertTag(m)}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left transition-colors"
                        data-testid="tag-member-option"
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={m.profile_image ? getImageUrl(m.profile_image) : undefined} />
                          <AvatarFallback className="bg-[#5b7f95] text-white text-xs">
                            {(m.display_name || m.full_name || '?').charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#2d3748] truncate">{m.display_name || m.full_name}</p>
                          {m.business_name && <p className="text-xs text-[#64748b] truncate">{m.business_name}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Image Previews */}
            {postImagePreviews.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {postImagePreviews.map((preview, i) => (
                  <div key={`preview-${i}-${preview.slice(-10)}`} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                    <img src={preview} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {postImages.length < MAX_IMAGES && (
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#5b7f95] transition-colors">
                    <Image className="w-5 h-5 text-gray-400" />
                    <input type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
                  </label>
                )}
                <p className="w-full text-xs text-[#64748b] mt-1">{postImages.length}/{MAX_IMAGES} images</p>
              </div>
            )}

            {/* PDF Preview */}
            {postFile && (
              <div className="relative mb-4 inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-xl">
                <FileText className="w-5 h-5 text-[#5b7f95]" />
                <span className="text-sm text-gray-700 max-w-[200px] truncate">{postFile.name}</span>
                <button type="button" onClick={removeFile} className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600 ml-2">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Video Preview */}
            {postVideoPreview && (
              <div className="relative mb-4">
                <video src={postVideoPreview} className="max-h-40 rounded-xl" controls />
                <button type="button" onClick={removeVideo}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-600 transition-colors">
                  <Image className="w-4 h-4" />
                  <span>Photos ({postImages.length}/{MAX_IMAGES})</span>
                </div>
                <input type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" data-testid="post-image-input" />
              </label>

              <label className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-600 transition-colors">
                  <Video className="w-4 h-4" />
                  <span>Video</span>
                </div>
                <input type="file" accept="video/*" onChange={handleVideoSelect} className="hidden" data-testid="post-video-input" />
              </label>

              <label className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-600 transition-colors">
                  <FileText className="w-4 h-4" />
                  <span>PDF</span>
                </div>
                <input type="file" accept=".pdf,application/pdf" onChange={handleFileSelect} className="hidden" data-testid="post-file-input" />
              </label>

              <Button
                type="submit"
                disabled={loading || (!newPost.trim() && postImages.length === 0 && !postFile && !postVideo)}
                className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full ml-auto"
                data-testid="create-post-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}

      {/* Posts Feed */}
      <div className="space-y-6">
        {posts.map((post) => {
          const postImgs = getPostImages(post);
          return (
            <Card key={post.id} className="border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="post-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={post.user_profile_image ? getImageUrl(post.user_profile_image) : undefined} />
                      <AvatarFallback className="bg-[#5b7f95] text-white">
                        {post.user_name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <Link
                        to={`/directory?member=${post.user_id}`}
                        className="font-semibold text-[#2d3748] hover:text-[#5b7f95] transition-colors cursor-pointer"
                        data-testid="post-author-link"
                      >
                        {post.user_name}
                      </Link>
                      <p className="text-sm text-[#64748b]">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  {(post.user_id === user.id || user.is_admin) && (
                    <div className="flex items-center gap-1">
                      {post.user_id === user.id && editingPostId !== post.id && (
                        <Button variant="ghost" size="sm" onClick={() => handleEditStart(post)}
                          className="text-[#5b7f95] hover:text-[#4a6b80]" data-testid="edit-post-btn">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)}
                        className="text-red-500 hover:text-red-600" data-testid="delete-post-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingPostId === post.id ? (
                  <div className="mb-4">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="bg-gray-50 border-gray-200 rounded-xl mb-2 min-h-[80px]"
                      data-testid="edit-post-textarea"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={handleEditCancel} className="rounded-full">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleEditSave(post.id)}
                        className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="save-edit-btn">
                        <Check className="w-4 h-4 mr-1" />Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <p className="text-[#2d3748] whitespace-pre-wrap">{renderContentWithLinks(post.content)}</p>
                    {post.edited_at && (
                      <p className="text-xs text-[#94a3b8] mt-1 italic">edited</p>
                    )}
                  </div>
                )}

                {/* Post Images */}
                {postImgs.length === 1 && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
                    onClick={() => openLightbox(postImgs, 0)} data-testid="post-image">
                    <img src={getImageUrl(postImgs[0])} alt="Post" className="w-full max-h-[400px] object-contain" />
                  </div>
                )}
                {postImgs.length === 2 && (
                  <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl overflow-hidden">
                    {postImgs.map((img, i) => (
                      <div key={`img2-${i}-${img.slice(-12)}`} className="bg-gray-100 cursor-pointer aspect-square"
                        onClick={() => openLightbox(postImgs, i)} data-testid="post-image">
                        <img src={getImageUrl(img)} alt={`Post ${i+1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
                {postImgs.length === 3 && (
                  <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl overflow-hidden">
                    <div className="row-span-2 bg-gray-100 cursor-pointer"
                      onClick={() => openLightbox(postImgs, 0)} data-testid="post-image">
                      <img src={getImageUrl(postImgs[0])} alt="Post 1" className="w-full h-full object-cover" />
                    </div>
                    {postImgs.slice(1).map((img, i) => (
                      <div key={`img3-${i}-${img.slice(-12)}`} className="bg-gray-100 cursor-pointer aspect-square"
                        onClick={() => openLightbox(postImgs, i+1)} data-testid="post-image">
                        <img src={getImageUrl(img)} alt={`Post ${i+2}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
                {postImgs.length >= 4 && (
                  <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl overflow-hidden">
                    {postImgs.slice(0, 4).map((img, i) => (
                      <div key={`img4-${i}-${img.slice(-12)}`} className="bg-gray-100 cursor-pointer aspect-square relative"
                        onClick={() => openLightbox(postImgs, i)} data-testid="post-image">
                        <img src={getImageUrl(img)} alt={`Post ${i+1}`} className="w-full h-full object-cover" />
                        {i === 3 && postImgs.length > 4 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-2xl font-bold">+{postImgs.length - 4}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Post Video */}
                {post.video && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-gray-100">
                    <video src={post.video} controls className="w-full max-h-[400px]" preload="metadata" data-testid="post-video" />
                  </div>
                )}

                {/* Post PDF/File */}
                {post.file_url && (
                  <a href={post.file_url} target="_blank" rel="noopener noreferrer"
                    className="mb-4 flex items-center gap-3 bg-gray-50 hover:bg-gray-100 p-4 rounded-xl border border-gray-200 transition-colors">
                    <div className="bg-[#5b7f95] p-3 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2d3748] truncate">{post.file_name || 'Download PDF'}</p>
                      <p className="text-sm text-[#64748b]">Click to view/download</p>
                    </div>
                  </a>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4 mb-4 pt-4 border-t border-gray-100">
                  <Button variant="ghost" size="sm" onClick={() => handleLike(post.id)}
                    className={`rounded-full ${post.likes.includes(user.id) ? 'text-red-500' : 'text-gray-600'}`}
                    data-testid="like-post-btn">
                    <Heart className="w-4 h-4 mr-1" fill={post.likes.includes(user.id) ? 'currentColor' : 'none'} />
                    {post.likes.length}
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-full text-gray-600">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {post.comments.length}
                  </Button>
                </div>

                {/* Comments */}
                {post.comments.length > 0 && (
                  <div className="space-y-3 mb-4 pl-4 border-l-2 border-gray-100">
                    {post.comments.map((comment) => (
                      <div key={comment.id} className="text-sm">
                        <p className="font-semibold text-[#2d3748]">{comment.user_name}</p>
                        <p className="text-[#64748b]">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentInputs[post.id] || ''}
                    onChange={(e) => setCommentInputs({ ...commentInputs, [post.id]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleComment(post.id); }}
                    placeholder="Add a comment..."
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full focus:border-[#5b7f95] focus:ring-2 focus:ring-[#5b7f95]/20 outline-none"
                    data-testid="comment-input"
                  />
                  <Button onClick={() => handleComment(post.id)} size="sm"
                    className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="add-comment-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {posts.length === 0 && (
          <div className="text-center py-12 text-[#64748b]">
            <p>No posts yet. Be the first to share!</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 bg-black/40 rounded-full p-2"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Previous button */}
          {lightbox.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length });
              }}
              className="absolute left-4 text-white/80 hover:text-white z-10 bg-black/40 rounded-full p-2"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Image */}
          <img
            src={getImageUrl(lightbox.images[lightbox.index])}
            alt="Full size"
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {lightbox.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length });
              }}
              className="absolute right-4 text-white/80 hover:text-white z-10 bg-black/40 rounded-full p-2"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Counter */}
          {lightbox.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-4 py-1 rounded-full">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
